/**
 * useFixtureState - Custom hook for managing Fixture page state
 * Centralizes all state variables and setters for the Fixtures feature
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { INITIAL_FILTERS, VIEW_MODES, getInitialDateRange } from '../constants';
import { getBracketMatchLabel } from '../../../utils/fixtureHelpers';

const useFixtureState = () => {
  // Redux state
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  // View state
  const [viewMode, setViewMode] = useState(VIEW_MODES.LIST);
  const initialDateRange = getInitialDateRange(selectedTournament);
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);

  // Re-initialize date range when tournament changes
  const prevTournamentId = useRef(selectedTournament?.id || selectedTournament?._id);
  useEffect(() => {
    const currentId = selectedTournament?.id || selectedTournament?._id;
    if (currentId && currentId !== prevTournamentId.current) {
      prevTournamentId.current = currentId;
      const range = getInitialDateRange(selectedTournament);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  }, [selectedTournament]);

  // Filter state
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showConflicts, setShowConflicts] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isAddResultModalOpen, setIsAddResultModalOpen] = useState(false);

  // Selected fixture for modals
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  // Compute filter options from category data
  const filterOptions = useMemo(() => {
    if (!selectedCategory) {
      return { phases: [], brackets: [], pitches: [], teams: [] };
    }

    const phases = selectedCategory.phases?.map((p) => p.name) || [];
    const brackets = [...new Set(
      selectedCategory.phases?.filter((p) => p.bracket).map((p) => p.bracket) || []
    )];
    const pitches = selectedTournament?.matchVenue?.pitches || [];
    const teams = selectedCategory.teams?.map((t) => t.teamId?.name || t.teamId?.title) || [];

    return { phases, brackets, pitches, teams };
  }, [selectedCategory, selectedTournament]);

  // Get all fixtures with enriched data
  const allFixtures = useMemo(() => {
    if (!selectedCategory) return [];

    const fixtures = [];
    const teamsMap = new Map();
    let phaseCollected = 0;
    let rootCollected = 0;
    let rootSkippedAsDuplicate = 0;

    // Helper to normalize date strings
    const normalizeMatchDate = (value) => {
      if (!value) return undefined;
      // Handle "YYYY-MM-DD" string
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) return value;
      // Handle ISO strings
      if (typeof value === 'string' && value.includes('T')) return value.substring(0, 10);

      const m = moment(value);
      return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
    };

    // Helper to normalize time strings to HH:mm
    const normalizeMatchTime = (value) => {
      if (!value) return undefined;
      // Handle "HH:mm" or "HH:mm:ss" string
      if (typeof value === 'string') {
        if (value.match(/^\d{1,2}:\d{2}/)) return value.substring(0, 5);
        if (value.includes('T')) {
          const timePart = value.split('T')[1];
          if (timePart) return timePart.substring(0, 5);
        }
        return value; // Return as is if we can't parse but it's a string, though preferably HH:mm
      }

      const m = moment(value);
      return m.isValid() ? m.format('HH:mm') : undefined;
    };

    // Helper to extract date/time from fixture object
    const normalizeDateTime = (fixture) => {
      // matchTime is now a UTC ISO instant — use it as the source of both
      // calendar date and clock time, falling back to legacy event refs.
      const rawDateTime =
        fixture?.matchTime ||
        fixture?.eventDateTime ||
        fixture?.eventId?.eventDateTime ||
        fixture?.eventId?.meetDateTime ||
        fixture?.match_date ||
        fixture?.date ||
        null;

      const rawTime =
        fixture?.matchTime ||
        fixture?.match_time ||
        fixture?.time ||
        fixture?.startTime ||
        fixture?.start_time ||
        null;

      const matchDate = normalizeMatchDate(rawDateTime);
      // Prioritize explicit time if available
      let matchTime = normalizeMatchTime(rawTime);
      if (!matchTime) {
        matchTime = normalizeMatchTime(rawDateTime);
      }

      return { matchDate, matchTime };
    };

    const getFixtureKey = (fixture, matchDate, matchTime) => {
      const id = fixture?._id || fixture?.id;
      if (id) return `id:${id}`;

      const homeId =
        fixture?.homeTeamId?.toString?.() ||
        fixture?.homeTeamActualId?.toString?.() ||
        (typeof fixture?.homeTeamId === 'string' ? fixture.homeTeamId : '');
      const awayId =
        fixture?.awayTeamId?.toString?.() ||
        fixture?.awayTeamActualId?.toString?.() ||
        (typeof fixture?.awayTeamId === 'string' ? fixture.awayTeamId : '');
      const pitch = typeof fixture?.pitch === 'string' ? fixture.pitch : '';

      return `sig:${matchDate || ''}|${matchTime || ''}|${homeId || ''}|${awayId || ''}|${pitch || ''}`;
    };

    // Build teams map for quick lookup - maps various ID forms to team details
    selectedCategory.teams?.forEach((t) => {
      // Logic: t.teamId usually contains the populated team object (with title, crest)
      // t._id is the category-team entry ID
      const teamObj = t.teamId || t.team || t;
      const teamData = {
        _id: teamObj._id || teamObj.id,
        title: teamObj.title || teamObj.name || t.teamTitle || "Unknown Team",
        crest: teamObj.crest || teamObj.logo || t.teamCrest,
        email: teamObj.email
      };

      if (teamData._id) {
        teamsMap.set(teamData._id.toString(), teamData);
      }

      // Also map the category entry ID to this team data
      if (t._id) teamsMap.set(t._id.toString(), teamData);
      if (t.id) teamsMap.set(t.id.toString(), teamData);

      // Also map the teamId object's ID directly if it exists (nested population)
      if (t.teamId && t.teamId._id) {
        teamsMap.set(t.teamId._id.toString(), teamData);
      }
    });

    // Helper to resolve team data from fixture
    const resolveTeam = (keyPrefix, fixture) => {
      // 1. Try existing object on fixture first
      const prop = fixture[keyPrefix];
      if (prop && (prop.title || prop.name)) {
        return prop;
      }

      // 2. Try resolving by ID
      let id =
        fixture[`${keyPrefix}Id`] ||
        fixture[`${keyPrefix}ActualId`] ||
        (prop?._id ? prop._id : prop);

      if (typeof id === 'object' && id !== null) {
        id = id._id || id.id;
      }

      if (id) {
        const mapped = teamsMap.get(id.toString());
        if (mapped) return mapped;
      }

      // 3. Fallback: use snapshot fields (homeTeamName/awayTeamName, homeTeamCrest/awayTeamCrest)
      const snapshotName = fixture[`${keyPrefix}Name`];
      const snapshotCrest = fixture[`${keyPrefix}Crest`];
      if (snapshotName) {
        return { title: snapshotName, crest: snapshotCrest, isPlaceholder: true };
      }

      // 4. Fallback: Check if the property itself is the name
      if (typeof prop === 'string' && prop.length > 2 && prop !== 'TBD') {
        return { title: prop, isPlaceholder: true };
      }

      return null;
    };

    // Collect fixtures from all phases and groups (if phases exist)
    selectedCategory.phases?.forEach((phase) => {
      phase.blocks?.forEach((group) => {
        // For BRACKET blocks, fixtures are nested in rounds[].fixtures[].
        // Compute a per-fixture match label (e.g. "QF-2", "SF-1", "Final") so
        // bracket cards can show the match name in place of the bracket name.
        // Position is 1-indexed within a round, deduplicated by tieNumber so
        // both legs of the same tie share the same label.
        const groupFixtures = group.type === "BRACKET" && group.rounds
          ? group.rounds.flatMap((round) => {
              const tiePos = new Map();
              return (round.fixtures || []).map((f) => {
                let positionInRound;
                if (f.tieNumber != null) {
                  if (!tiePos.has(f.tieNumber)) tiePos.set(f.tieNumber, tiePos.size + 1);
                  positionInRound = tiePos.get(f.tieNumber);
                } else {
                  tiePos.set(Symbol(), tiePos.size + 1);
                  positionInRound = tiePos.size;
                }
                return {
                  ...f,
                  roundName: round.name,
                  __matchLabel: f.name || getBracketMatchLabel(round.name, positionInRound),
                };
              });
            })
          : (group.fixtures || []);

        groupFixtures.forEach((fixture) => {
          // Skip cancelled fixtures
          if (fixture.status === 'CANCELLED') return;
          // Skip bracket placeholder fixtures that have no scheduled date
          if (group.type === 'BRACKET' && fixture.status === 'WAITING_FOR_TEAMS' && !fixture.matchTime) return;

          const { matchDate, matchTime: matchTimeStr } = normalizeDateTime(fixture);
          const key = getFixtureKey(fixture, matchDate, matchTimeStr);

          const homeTeam = resolveTeam('homeTeam', fixture);
          const awayTeam = resolveTeam('awayTeam', fixture);

          // IMPORTANT: don't overwrite fixture.matchTime — it's a UTC ISO instant
          // that downstream code needs intact. Stash the derived strings in
          // separate __ fields used for sorting/grouping/keys.
          fixtures.push({
            ...fixture,
            __matchDate: matchDate,
            __matchTime: matchTimeStr,
            __fixtureKey: key,
            phaseId: phase._id,
            phaseName: phase.name,
            blockId: group._id,
            // For bracket fixtures, show the match label ("QF-2", "SF-1", ...)
            // instead of the bracket block's own name.
            groupName: fixture.__matchLabel || group.name,
            blockType: group.type,
            homeTeam: homeTeam || { title: "TBD" },
            awayTeam: awayTeam || { title: "TBD" },
            hasResult:
              fixture.hasResult ||
              fixture.status === 'COMPLETED' ||
              fixture.homeTeamScore != null ||
              fixture.eventId?.homeTeam?.scores != null,
          });
          phaseCollected += 1;
        });
      });
    });

    // Also collect fixtures at the category root level (created directly or auto-generated)
    selectedCategory.fixtures?.forEach((fixture) => {
      // Skip cancelled fixtures
      if (fixture.status === 'CANCELLED') return;

      const { matchDate, matchTime: matchTimeStr } = normalizeDateTime(fixture);

      const incomingKey = getFixtureKey(fixture, matchDate, matchTimeStr);
      if (fixtures.some((f) => (f?.__fixtureKey || getFixtureKey(f, f.__matchDate, f.__matchTime)) === incomingKey)) {
        rootSkippedAsDuplicate += 1;
        return;
      }

      const homeTeam = resolveTeam('homeTeam', fixture);
      const awayTeam = resolveTeam('awayTeam', fixture);

      fixtures.push({
        ...fixture,
        __matchDate: matchDate,
        __matchTime: matchTimeStr,
        __fixtureKey: incomingKey,
        phaseId: null,
        phaseName: 'General',
        blockId: null,
        groupName: 'General',
        homeTeam: homeTeam || { title: "TBD" },
        awayTeam: awayTeam || { title: "TBD" },
        hasResult:
          fixture.hasResult ||
          fixture.status === 'COMPLETED' ||
          fixture.homeTeamScore != null ||
          fixture.eventId?.homeTeam?.scores != null,
      });
      rootCollected += 1;
    });


    return fixtures;
  }, [selectedCategory]);

  // Apply filters to fixtures
  const filteredFixtures = useMemo(() => {
    let result = [...allFixtures];

    // Filter by category if selected
    if (selectedCategoryFilter) {
      result = result.filter((f) => f.groupCategory === selectedCategoryFilter);
    }

    if (filters.phases.length > 0) {
      result = result.filter((f) => filters.phases.includes(f.phaseName));
    }

    if (filters.brackets.length > 0) {
      result = result.filter((f) => filters.brackets.includes(f.bracket));
    }

    if (filters.pitches.length > 0) {
      result = result.filter((f) => filters.pitches.includes(f.pitch));
    }

    if (filters.teams.length > 0) {
      result = result.filter(
        (f) =>
          filters.teams.includes(f.homeTeam?.name) ||
          filters.teams.includes(f.homeTeam?.title) ||
          filters.teams.includes(f.awayTeam?.name) ||
          filters.teams.includes(f.awayTeam?.title)
      );
    }

    const toMs = (f) => (f.matchTime ? new Date(f.matchTime).valueOf() : 0);
    result.sort((a, b) => toMs(a) - toMs(b));

    return result;
  }, [allFixtures, filters, selectedCategoryFilter]);

  // Group fixtures by date, filtered to the selected date range
  const fixturesByDate = useMemo(() => {
    const grouped = {};

    filteredFixtures.forEach((fixture) => {
      // matchTime is a UTC ISO instant — derive viewer-local calendar day.
      const date = fixture.matchTime && moment(fixture.matchTime).isValid()
        ? moment(fixture.matchTime).format('YYYY-MM-DD')
        : undefined;
      if (!date) return;

      // Apply date range filter
      if (startDate && date < startDate) return;
      if (endDate && date > endDate) return;

      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(fixture);
    });

    return grouped;
  }, [filteredFixtures, startDate, endDate]);

  // Sorted dates
  const sortedDates = useMemo(() => {
    return Object.keys(fixturesByDate).sort();
  }, [fixturesByDate]);

  // Detect pitch conflicts
  const pitchConflicts = useMemo(() => {
    const conflicts = new Map();

    Object.values(fixturesByDate).forEach((dayFixtures) => {
      const pitchGroups = {};
      dayFixtures.forEach((f) => {
        if (f.pitch) {
          if (!pitchGroups[f.pitch]) {
            pitchGroups[f.pitch] = [];
          }
          pitchGroups[f.pitch].push(f);
        }
      });

      Object.values(pitchGroups).forEach((pitchFixtures) => {
        pitchFixtures.sort((a, b) => a.matchTime?.localeCompare(b.matchTime));

        for (let i = 0; i < pitchFixtures.length - 1; i++) {
          const current = pitchFixtures[i];
          const next = pitchFixtures[i + 1];

          if (current.matchTime === next.matchTime) {
            conflicts.set(current._id, true);
            conflicts.set(next._id, true);
          }
        }
      });
    });

    return conflicts;
  }, [fixturesByDate]);

  // Add conflict flags to fixtures
  const fixturesWithConflicts = useMemo(() => {
    const result = {};

    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      result[date] = fixtures.map((f) => ({
        ...f,
        hasPitchConflict: pitchConflicts.has(f._id),
      }));
    });

    return result;
  }, [fixturesByDate, pitchConflicts]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    return (
      filters.phases.length +
      filters.brackets.length +
      filters.pitches.length +
      filters.teams.length
    );
  }, [filters]);

  // Filter handlers
  const handlePhaseFilterChange = useCallback((phase, isAdd) => {
    setFilters((prev) => ({
      ...prev,
      phases: isAdd ? [...prev.phases, phase] : prev.phases.filter((p) => p !== phase),
    }));
  }, []);

  const handleBracketFilterChange = useCallback((bracket, isAdd) => {
    setFilters((prev) => ({
      ...prev,
      brackets: isAdd ? [...prev.brackets, bracket] : prev.brackets.filter((b) => b !== bracket),
    }));
  }, []);

  const handlePitchFilterChange = useCallback((pitch, isAdd) => {
    setFilters((prev) => ({
      ...prev,
      pitches: isAdd ? [...prev.pitches, pitch] : prev.pitches.filter((p) => p !== pitch),
    }));
  }, []);

  const handleTeamFilterChange = useCallback((team, isAdd) => {
    setFilters((prev) => ({
      ...prev,
      teams: isAdd ? [...prev.teams, team] : prev.teams.filter((t) => t !== team),
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  // Date navigation handlers
  const handleDateChange = useCallback((type, value) => {
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  }, []);

  const handleShiftDates = useCallback((days) => {
    setStartDate((prev) => moment(prev).add(days, 'days').format('YYYY-MM-DD'));
    setEndDate((prev) => moment(prev).add(days, 'days').format('YYYY-MM-DD'));
  }, []);

  // Modal handlers
  const openCreateModal = useCallback(() => setIsCreateModalOpen(true), []);
  const closeCreateModal = useCallback(() => setIsCreateModalOpen(false), []);

  const openEditModal = useCallback((fixture) => {
    setSelectedFixture(fixture);
    setIsEditModalOpen(true);
  }, []);
  const closeEditModal = useCallback(() => {
    setSelectedFixture(null);
    setIsEditModalOpen(false);
  }, []);

  const openDeleteModal = useCallback((fixture) => {
    setSelectedFixture(fixture);
    setIsDeleteModalOpen(true);
  }, []);
  const closeDeleteModal = useCallback(() => {
    setSelectedFixture(null);
    setIsDeleteModalOpen(false);
  }, []);

  const openGenerateModal = useCallback(() => setIsGenerateModalOpen(true), []);
  const closeGenerateModal = useCallback(() => setIsGenerateModalOpen(false), []);

  const openAddResultModal = useCallback((fixture) => {
    setSelectedFixture(fixture);
    setIsAddResultModalOpen(true);
  }, []);

  const openEditResultModal = useCallback((fixture, result) => {
    setSelectedFixture(fixture);
    setSelectedResult(result);
    setIsAddResultModalOpen(true);
  }, []);

  const closeAddResultModal = useCallback(() => {
    setSelectedFixture(null);
    setSelectedResult(null);
    setIsAddResultModalOpen(false);
  }, []);

  const openFilterPanel = useCallback(() => setIsFilterPanelOpen(true), []);
  const closeFilterPanel = useCallback(() => setIsFilterPanelOpen(false), []);

  return {
    selectedTournament,
    selectedCategory,
    viewMode,
    setViewMode,
    startDate,
    endDate,
    handleDateChange,
    handleShiftDates,
    filters,
    showConflicts,
    setShowConflicts,
    isFilterPanelOpen,
    openFilterPanel,
    closeFilterPanel,
    filterOptions,
    activeFiltersCount,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    handlePhaseFilterChange,
    handleBracketFilterChange,
    handlePitchFilterChange,
    handleTeamFilterChange,
    clearAllFilters,
    allFixtures,
    filteredFixtures,
    fixturesByDate: fixturesWithConflicts,
    sortedDates,
    pitchConflicts,
    isCreateModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isGenerateModalOpen,
    isAddResultModalOpen,
    selectedFixture,
    selectedResult,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    openGenerateModal,
    closeGenerateModal,
    openAddResultModal,
    openEditResultModal,
    closeAddResultModal,
  };
};

export default useFixtureState;
